import React from 'react';
import { Trans } from 'react-i18next';

import { BuildingWithConvertedAddresses } from '../utils/types';
import { CloseIcon } from '../common/CloseIcon';
import OffMarketBuyAndSellDetailsModal from './OffMarketBuyAndSellDetailsModal';
import SharePromptModal from '../common/SharePromptModal';
import RemoveSellReasonModal from '../common/RemoveSellReasonModal';
import { formatAddressString } from '../../utils/shareUtils';
import { UseOffMarketIntentReturn } from './useOffMarketIntent';

type Props = {
  intent: UseOffMarketIntentReturn;
  selectedBuilding: BuildingWithConvertedAddresses;
  isLoggedIn: boolean;
  isLandingPage?: boolean;
  userEmail?: string;
  setLoginModalVisible: (visible: boolean) => void;
};

/**
 * Shared modal/dialog surface for off-market intent, driven by useOffMarketIntent.
 * Rendered by the Building view so the remove-confirm, details, share-prompt and
 * remove-reason flows stay identical regardless of which CTA triggered them.
 */
const OffMarketIntentModals: React.FC<Props> = ({
  intent,
  selectedBuilding,
  isLoggedIn,
  isLandingPage,
  userEmail,
  setLoginModalVisible,
}) => {
  return (
    <>
      {/* Remove confirmation dialog when toggling off an existing interest */}
      {intent.showDeleteConfirm && (
        <div className="end-chat-confirmation-overlay" onClick={() => intent.setShowDeleteConfirm(false)}>
          <div
            className="end-chat-confirmation"
            role="dialog"
            aria-modal="true"
            aria-labelledby="off-market-delete-confirm-title"
            aria-describedby="off-market-delete-confirm-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="end-chat-confirmation__header">
              <h3 id="off-market-delete-confirm-title">
                <Trans i18nKey="building.offMarket.modal.deleteConfirmTitle">Remove listing</Trans>
              </h3>
              <button
                className="end-chat-confirmation__close"
                onClick={() => intent.setShowDeleteConfirm(false)}
              >
                <CloseIcon color="#202125" size={16} />
              </button>
            </div>
            <div className="end-chat-confirmation__content">
              <p id="off-market-delete-confirm-desc">
                <Trans i18nKey="building.offMarket.modal.deleteConfirmText">Are you sure you want to remove this listing?</Trans>
              </p>
            </div>
            <div className="end-chat-confirmation__actions">
              <button
                className="end-chat-confirmation__button end-chat-confirmation__button--cancel"
                onClick={() => intent.setShowDeleteConfirm(false)}
              >
                <Trans i18nKey="building.offMarket.modal.cancel">Cancel</Trans>
              </button>
              <button
                className="end-chat-confirmation__button end-chat-confirmation__button--confirm"
                onClick={intent.handleDeleteConfirm}
              >
                <Trans i18nKey="building.offMarket.modal.delete">Remove</Trans>
              </button>
            </div>
          </div>
        </div>
      )}
      <OffMarketBuyAndSellDetailsModal
        isOpen={intent.modalOpen}
        isLandingPage={isLandingPage}
        onClose={() => intent.setModalOpen(false)}
        onConfirm={intent.handleModalConfirm}
        actionType={intent.currentAction || 'buy'}
        isLoading={
          (intent.currentAction === 'sell' && intent.isLoadingOffMarketSellWillingness) ||
          (intent.currentAction === 'buy' && intent.isLoadingOffMarketBuyWillingness)
        }
        building={selectedBuilding}
        isLoggedIn={isLoggedIn}
        setLoginModalVisible={setLoginModalVisible}
        willingnessId={intent.willingnessId}
      />
      <SharePromptModal
        isOpen={intent.showSharePrompt}
        onClose={intent.handleCloseSharePrompt}
        onShare={intent.handleShareWillingness}
        actionType={intent.createdWillingnessData?.actionType || 'buy'}
        buildingAddress={selectedBuilding.addresses?.[0] ? formatAddressString(selectedBuilding.addresses[0]) : undefined}
        isSharing={intent.isSharing}
        isMatch={intent.createdWillingnessData ? intent.createdWillingnessData.isMatch : false}
        eventId={intent.createdWillingnessData?.eventId}
        buildingId={intent.createdWillingnessData?.buildingId}
        userEmail={userEmail}
      />
      <RemoveSellReasonModal
        isOpen={intent.showRemoveSellReason}
        isRemoving={intent.isRemovingSell}
        onClose={() => intent.setShowRemoveSellReason(false)}
        onConfirm={intent.handleSellRemovalConfirm}
      />
    </>
  );
};

export default OffMarketIntentModals;
